import React, { useState, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supplierService } from '@services';
import { Supplier, UpdateSupplierRequest, ApiError } from '@app-types';
import { GenericForm, FormField } from '@components/common/forms';
import GenericModal from '@components/common/modals/GenericModal';
import Button from '@components/common/Button';
import { Textarea } from '@components/common';
import { getApiErrorMessage, splitApiFieldErrors } from '@utils/apiError';

interface EditSupplierFormData {
  vendor_id: string;
  name: string;
  tier_id: string;
  high_trust_supplier: boolean;
  critical_service_product: boolean;
}

interface EditSupplierFormProps {
  supplier: Supplier;
  contractsCount: number;
  onSubmit: (data: UpdateSupplierRequest, note: string) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  error?: ApiError;
}

export const EditSupplierForm: React.FC<EditSupplierFormProps> = ({
  supplier,
  contractsCount,
  onSubmit,
  onCancel,
  isSubmitting,
  error
}) => {
  // Track original values for comparison
  const originalTierId = useRef(supplier.tier_id);
  const originalUpdatedAt = useRef(supplier.updated_at);

  // State for tier change confirmation modal
  const [showTierConfirmation, setShowTierConfirmation] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<EditSupplierFormData | null>(null);

  // State for optimistic lock error
  const [optimisticLockError, setOptimisticLockError] = useState<string | null>(null);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  // State to track if we should revert tier
  const [revertTierTrigger, setRevertTierTrigger] = useState(0);

  // State to track current form data for hasChanges
  const [currentFormData, setCurrentFormData] = useState<EditSupplierFormData>({
    vendor_id: supplier.vendor_id || '',
    name: supplier.name,
    tier_id: supplier.tier_id.toString(),
    high_trust_supplier: supplier.high_trust_supplier || false,
    critical_service_product: supplier.critical_service_product || false
  });
  const [note, setNote] = useState('');

  // Calculate if form has changes
  const hasChanges = useMemo(() => {
    if (note.trim()) return true;
    if (currentFormData.name !== supplier.name) return true;
    if (Number.parseInt(currentFormData.tier_id, 10) !== supplier.tier_id) return true;
    if (currentFormData.high_trust_supplier !== (supplier.high_trust_supplier || false)) return true;
    if (currentFormData.critical_service_product !== (supplier.critical_service_product || false)) return true;
    return false;
  }, [currentFormData, supplier, note]);

  // Fetch tiers for dropdown
  const { data: tiers, isLoading: tiersLoading } = useQuery({
    queryKey: ['supplierTiers'],
    queryFn: () => supplierService.getSupplierTiers()
  });

  // Define form fields
  const editSupplierFormFields: FormField[] = [
    {
      name: 'vendor_id',
      label: 'Supplier Number',
      type: 'readonly',
      disabled: true
    },
    {
      name: 'name',
      label: 'Supplier Name',
      type: 'text',
      required: true,
      placeholder: 'Enter supplier name'
    },
    {
      name: 'tier_id',
      label: 'Tier',
      type: 'select',
      required: true,
      disabled: tiersLoading,
      options: [
        { value: '', label: 'Select Tier' },
        ...(tiers?.map((tier) => ({
          value: tier.id.toString(),
          label: tier.name
        })) || [])
      ]
    },
    {
      name: 'high_trust_supplier',
      label: '',
      type: 'checkbox',
      placeholder: 'High Trust Supplier'
    },
    {
      name: 'critical_service_product',
      label: '',
      type: 'checkbox',
      placeholder: 'Critical Service/Product'
    }
  ];

  // Handle form submission with optimistic locking check
  const handleFormSubmit = async (data: EditSupplierFormData) => {
    // If there's an optimistic lock error, prevent submission
    if (isSaveDisabled) {
      return;
    }

    // Check if tier changed and needs confirmation
    const newTierId = Number.parseInt(data.tier_id, 10);
    const tierChanged = newTierId !== originalTierId.current;

    if (tierChanged && contractsCount > 0) {
      // Store form data and show confirmation
      setPendingFormData(data);
      setShowTierConfirmation(true);
      return; // Don't submit yet
    }

    // No tier change or no contracts, proceed with save
    await performSave(data);
  };

  // Perform the actual save with optimistic locking check
  const performSave = async (data: EditSupplierFormData) => {
    try {
      // Clear any previous errors
      setOptimisticLockError(null);

      // Fetch fresh supplier data to check for concurrent updates
      const freshSupplierResponse = await supplierService.getSupplier(supplier.id);
      const freshSupplier = freshSupplierResponse.supplier;

      // Compare updated_at timestamps
      if (freshSupplier.updated_at !== originalUpdatedAt.current) {
        // Concurrent update detected
        const updatedBy = freshSupplier.updated_by_name || 'another user';
        setOptimisticLockError(`The supplier was recently updated by ${updatedBy}. Please reopen the edit form.`);
        setIsSaveDisabled(true);
        return;
      }

      // No concurrent updates, proceed with save
      const payload: UpdateSupplierRequest = {
        name: data.name,
        tier_id: Number.parseInt(data.tier_id, 10),
        high_trust_supplier: data.high_trust_supplier || false,
        critical_service_product: data.critical_service_product || false
      };

      onSubmit(payload, note);
    } catch (error) {
      console.error('Error checking for concurrent updates:', error);
      // Still allow save attempt if check fails
      const payload: UpdateSupplierRequest = {
        name: data.name,
        tier_id: Number.parseInt(data.tier_id, 10),
        high_trust_supplier: data.high_trust_supplier || false,
        critical_service_product: data.critical_service_product || false
      };
      onSubmit(payload, note);
    }
  };

  // Handle tier confirmation
  const handleTierConfirm = async () => {
    setShowTierConfirmation(false);
    if (pendingFormData) {
      await performSave(pendingFormData);
      setPendingFormData(null);
    }
  };

  // Handle tier cancellation
  const handleTierCancel = () => {
    setShowTierConfirmation(false);
    setPendingFormData(null);
    // Revert tier in form data state
    setCurrentFormData((prev) => ({
      ...prev,
      tier_id: supplier.tier_id.toString()
    }));
    // Trigger a revert of the tier field
    setRevertTierTrigger((prev) => prev + 1);
  };

  const getTierConfirmationMessage = () => {
    if (contractsCount <= 50) {
      return `Changing tier will update ${contractsCount} associated contract${
        contractsCount === 1 ? '' : 's'
      }. Continue?`;
    } else {
      return `Changing tier will update ${contractsCount} associated contracts. This may take a few moments. Continue?`;
    }
  };

  const { fieldErrors, residualMessages } = error
    ? splitApiFieldErrors(error, ['name', 'tier_id'])
    : { fieldErrors: {}, residualMessages: [] };
  const backendError =
    residualMessages.length > 0 || (error && Object.keys(fieldErrors).length === 0)
      ? residualMessages.join(' ') ||
        getApiErrorMessage(error, 'An error occurred while updating the supplier. Please try again.')
      : null;

  return (
    <>
      {optimisticLockError && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626'
          }}
        >
          <strong>Error:</strong> {optimisticLockError}
        </div>
      )}

      {backendError && !optimisticLockError && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626'
          }}
        >
          <strong>Error:</strong> {backendError}
        </div>
      )}

      <GenericForm
        key={revertTierTrigger} // Force re-render to revert tier
        fields={editSupplierFormFields}
        initialData={{
          vendor_id: supplier.vendor_id || '',
          name: supplier.name,
          tier_id: supplier.tier_id.toString(),
          high_trust_supplier: supplier.high_trust_supplier || false,
          critical_service_product: supplier.critical_service_product || false
        }}
        onSubmit={handleFormSubmit}
        onCancel={onCancel}
        isSubmitting={isSubmitting}
        submitLabel="Save Changes"
        submitDisabled={isSaveDisabled || !hasChanges}
        externalErrors={fieldErrors}
        onFieldChange={(name, value) => {
          setCurrentFormData((prev) => ({ ...prev, [name]: value }));
        }}
      >
        <Textarea
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optionally record the reason or context for this supplier update"
          rows={3}
          dataTestId="supplier-note"
        />
      </GenericForm>

      {/* Tier Change Confirmation Modal */}
      <GenericModal isOpen={showTierConfirmation} onClose={handleTierCancel} title="Confirm Tier Change" size="sm">
        <div>
          <p style={{ marginBottom: '20px' }}>{getTierConfirmationMessage()}</p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={handleTierCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleTierConfirm}>
              Continue
            </Button>
          </div>
        </div>
      </GenericModal>
    </>
  );
};
